import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  isStaleChunk: boolean
}

/**
 * Detecta erros de chunk lazy quebrado por deploy novo invalidando hashes.
 * Ex: "Failed to fetch dynamically imported module: .../assets/X-hash.js"
 * Em SPA, o HTML cached aponta pra um chunk que sumiu no novo deploy.
 * Resposta: força reload com cache-bust (faz o browser pegar o novo index.html).
 */
function isStaleChunkError(error: Error): boolean {
  const msg = error.message || ''
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading CSS chunk') ||
    /Unable to preload (CSS|module)/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  )
}

/**
 * Recarrega uma vez com cache-bust. Evita loop infinito via flag em sessionStorage.
 */
function autoReloadOnce(): boolean {
  try {
    const KEY = '__stale_chunk_reloaded'
    if (sessionStorage.getItem(KEY) === '1') return false
    sessionStorage.setItem(KEY, '1')
    const u = new URL(window.location.href)
    u.searchParams.set('_v', String(Date.now()))
    window.location.replace(u.toString())
    return true
  } catch {
    return false
  }
}

export class ErrorBoundaryInner extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { error: null, isStaleChunk: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { error, isStaleChunk: isStaleChunkError(error) }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
    if (isStaleChunkError(error)) {
      // Tenta auto-recarregar uma vez. Se já tentou (loop), cai pro UI normal.
      autoReloadOnce()
    }
  }

  render() {
    const { error, isStaleChunk } = this.state
    if (!error) return this.props.children

    if (isStaleChunk) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center p-8">
          <div className="w-full max-w-lg rounded-xl border border-amber-200 bg-amber-50 p-8 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <RefreshCw className="h-5 w-5 text-amber-700 animate-spin" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">Atualizando o app...</h2>
                <p className="text-sm text-foreground-muted">
                  Uma versão nova foi publicada. Vamos recarregar a página automaticamente.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                try { sessionStorage.removeItem('__stale_chunk_reloaded') } catch { /* ignore */ }
                window.location.href = window.location.pathname
              }}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
            >
              <RefreshCw className="h-4 w-4" />
              Recarregar agora
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <div className="w-full max-w-lg rounded-xl border border-red-200 bg-surface p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Algo deu errado nesta página</h2>
              <p className="text-sm text-foreground-muted">Um erro inesperado impediu a renderização</p>
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-red-50 px-4 py-3">
            <p className="font-mono text-xs text-red-700 break-all">{error.message}</p>
          </div>

          <button
            onClick={() => {
              this.setState({ error: null, isStaleChunk: false })
              window.location.reload()
            }}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <RefreshCw className="h-4 w-4" />
            Recarregar página
          </button>
        </div>
      </div>
    )
  }
}

// Export the original class for backwards compatibility and use in RouteAwareErrorBoundary
export const ErrorBoundary = ErrorBoundaryInner

/**
 * RouteAwareErrorBoundary automatically resets when the route changes.
 * This eliminates the need for users to manually reload (SHIFT+F5) after an error.
 *
 * The trick is using location.pathname as the key:
 * when pathname changes, React unmounts and remounts the ErrorBoundary,
 * clearing its error state in the process.
 */
export function RouteAwareErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  return (
    <ErrorBoundaryInner key={location.pathname}>
      {children}
    </ErrorBoundaryInner>
  )
}

// Limpa flag de reload quando a página é carregada com sucesso
// (depois de 5s sem erro, considera carga estável e permite novo reload futuro)
if (typeof window !== 'undefined') {
  setTimeout(() => {
    try { sessionStorage.removeItem('__stale_chunk_reloaded') } catch { /* ignore */ }
  }, 5000)
}
